import React, { useEffect, useMemo } from 'react'
import { Button, TabMenu } from '@demandlocal/components'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import Modal from '../../../components/Modal/Modal'
import { useNavigate, useParams } from 'react-router'
import { FormProvider, useForm } from 'react-hook-form'
import EditUserInfo from './EditUserInfo'
import EditUserOrganizations from './EditUserOrganizations'
import { useGetUserById } from '../../../api/user/useGetUserById'
import { useGetRoles } from '../../../api/utils/useGetRoles'
import { Role } from '../../../lib/types/Role'
import { find, omit } from 'lodash'
import { toast } from 'react-hot-toast'
import { useEditUser } from '../../../api/admin/useEditUser'
import { usePostValidateEmail } from '../../../api/admin/usePostValidateEmail'
import { useUnlockUserAccount } from '../../../api/admin/useUnlockUserAccount'
import EditUserPermissions from './EditUserPermissions'
import { queryClient } from '../../../api/queryClient'

export default function AdminUserEdit(): JSX.Element {
  const { user_id } = useParams()
  const formContext = useForm({
    mode: 'onSubmit',
    shouldUnregister: false,
  })

  const { data: user, isLoading, refetch: refetchUser } = useGetUserById(user_id)
  const { data: roles } = useGetRoles()
  const { mutateAsync: editUser } = useEditUser()
  const { mutateAsync: postValidateEmail } = usePostValidateEmail()
  const { mutateAsync: unlockAccount } = useUnlockUserAccount()

  const rolesOptions = useMemo(
    () =>
      roles?.map((role: Role) => ({
        label: role.user_role,
        value: role.id,
      })) ?? [],
    [roles],
  )

  const {
    reset,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = formContext
  const navigate = useNavigate()

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.documentElement.style.overflow = 'unset'
    }
  }, [])

  useEffect(() => {
    if (!isLoading) {
      reset({
        ...omit(user, ['organizations']),
        organizations: user?.organizations?.map((organization) => ({
          ...organization,
          label: organization?.name,
          value: organization?.id,
          is_initially_assigned: true,
          dealerships: organization?.dealerships?.map((dealership) => ({
            ...dealership,
            checked: true,
          })),
        })),
      })
    }
  }, [reset, user, isLoading])

  const user_role = watch('role_id')

  useEffect(() => {
    setValue(
      'role_id',
      find(rolesOptions, (role) => role.value === user?.role_id),
    )
  }, [rolesOptions, setValue, user])

  const onSubmit = async (values) => {
    const toastId = toast.loading('Saving user...')
    if (values.organizations.length === 0 && values.role_id?.value !== 1) {
      setError('organizations', {
        type: 'manual',
        message: 'You must select at least one organization',
      })
      toast.error('You must select at least one organization', {
        id: toastId,
      })
      return
    }
    if (values.email !== user?.email) {
      const validateEmailResponse = await postValidateEmail({
        data: { email: values.email },
      })
      if (!validateEmailResponse?.valid) {
        toast.dismiss(toastId)
        return setError('email', {
          type: 'manual',
          message: 'This email is already in use.',
        })
      }
    }
    const organizations = values.organizations
      .filter((org) => org.has_all_dealerships_assigned)
      .map((org) => org.value)
    const dealerships = values.organizations
      .filter((org) => !org.has_all_dealerships_assigned)
      .map((org) =>
        org.dealerships
          .filter((dealership) => dealership.checked)
          .map((dealership) => ({
            dealership_id: dealership.id,
            organization_id: org.value,
          })),
      )
      .flat()
    if (user?.id) {
      await editUser(
        {
          user_id: user?.id,
          data: {
            ...values,
            role_id: values.role_id?.value,
            organizations: organizations,
            dealerships: dealerships,
          },
        },
        {
          onSuccess: () => {
            toast.success('User edited successfully', {
              id: toastId,
            })
            queryClient.refetchQueries(['currentUser'])
            navigate('../')
          },
          onError: (error: any) => {
            toast.dismiss(toastId)
            Object.entries(error?.response?.data ?? []).forEach(([key, value]) => {
              setError(key as keyof typeof user, { message: value as string })
            })
          },
        },
      )
    }
  }

  const onUnlockAccount = async (e) => {
    e.preventDefault()
    const toastId = toast.loading('Unlocking account...')
    try {
      if (user_id) {
        await unlockAccount(
          { user_id },
          {
            onSuccess: async () => {
              await refetchUser()
              toast.success('Account unlocked', { id: toastId })
            },
            onError: () => {
              toast.error('Error unlocking account', { id: toastId })
            },
          },
        )
      }
    } catch (err) {
      toast.error('Error unlocking account')
    }
  }

  return (
    <Modal isOpen={true} key={'AdminUserEdit'}>
      <FormProvider {...formContext}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={'flex flex-1 flex-col h-screen relative'}>
          {Number(user?.is_locked) > 0 && (
            <div
              className={
                'absolute w-full h-full bg-white/90 z-10 flex items-center justify-center'
              }>
              <div className={'py-4 flex items-center justify-center flex-col'}>
                <h3 className={'text-primary-xiketic text-xl pb-2 font-bold'}>
                  Account was locked due to too many failed login attempts.
                </h3>
                <Button variant={'primary'} onClick={onUnlockAccount}>
                  Unlock account
                </Button>
              </div>
            </div>
          )}
          <div
            className={
              'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full h-screen'
            }>
            <div className={'text-3xl font-bold mt-12'}>
              Edit {user?.first_name} {user?.last_name}
            </div>
            <div className={'mt-12 mb-10'}>
              <TabMenu
                options={[
                  { name: 'Info', to: 'info' },
                  {
                    name: 'Organizations',
                    to: 'organizations',
                    visible: (user_role?.value || 0) > 1,
                    hasError: !!errors.organizations,
                  },
                  {
                    name: 'Permissions',
                    to: 'permissions',
                    visible: (user_role?.value || 0) > 1,
                    hasError: !!errors.organizations,
                  },
                ]}
              />
            </div>
            <div className={'font-Spartan overflow-scroll h-full'}>
              <Routes>
                <Route
                  path={'info'}
                  element={<EditUserInfo rolesOptions={rolesOptions} />}
                />
                <Route path={'organizations'} element={<EditUserOrganizations />} />
                <Route path={'permissions'} element={<EditUserPermissions />} />
                <Route path={'*'} element={<Navigate to={'info'} />} />
              </Routes>
            </div>
            <div className={'flex justify-between py-5 border-t-2'}>
              <Button
                type={'button'}
                variant={'secondary'}
                onClick={() => {
                  navigate('../')
                }}>
                Cancel
              </Button>
              <Button variant={'primary'}>Save</Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  )
}
